import {
  AlignItems,
  Body2,
  Bold,
  Box,
  Color,
  Display,
  FlexDirection,
  FontWeight,
  Heading2,
  Icon,
  IconVariant,
  JustifyContent,
  Space,
  Text,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { DashboardExperienceOrderResponse } from '@library/api-dashboard-experience'
import React from 'react'
import { getDisplayedStateTitle, isSkipOrder } from '../orderTileState'

type OrderTilePriceProps = {
  currentPrice: string | null
  originalPrice: string | null
  priceKind: string | null
}

const getOrderStateTitleColor = (order: DashboardExperienceOrderResponse) => {
  if (order.state === 'choose') {
    return Color.Warning_900
  }

  if (order.state === 'confirmed') {
    return Color.ColdGrey_600
  }

  if (isSkipOrder(order)) {
    return Color.Warning_900
  }

  return Color.Success_900
}

const OrderTilePrice = ({ currentPrice, originalPrice, priceKind }: OrderTilePriceProps) => {
  if (!currentPrice && !originalPrice) {
    return null
  }

  const isDiscountBadge = priceKind === 'discount' && Boolean(currentPrice)
  const shouldHideAllPrices = priceKind === null
  const shouldHideCurrentPrice = priceKind === 'price' && Boolean(originalPrice)

  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      alignItems={AlignItems.FlexEnd}
    >
      {!shouldHideAllPrices && !isDiscountBadge && originalPrice ? (
        <Text
          size={0}
          display={Display.Inline}
          textAlign={TextAlign.Right}
          color={Color.ColdGrey_600}
        >
          <s>{originalPrice}</s>
        </Text>
      ) : null}
      <Space size={0} direction="vertical" />
      {isDiscountBadge ? (
        <Box
          width="60px"
          height="60px"
          bg={Color.Success_800}
          borderRadius="round"
          display={Display.Flex}
          alignItems={AlignItems.Center}
          justifyContent={JustifyContent.Center}
          data-testid="discount-badge"
        >
          <Heading2
            size={[2, 3]}
            textAlign={TextAlign.Center}
            color={Color.White}
            style={{ whiteSpace: 'pre-line' }}
          >
            {currentPrice}
          </Heading2>
        </Box>
      ) : currentPrice && !shouldHideAllPrices && !shouldHideCurrentPrice ? (
        <Text
          size={[4, 9]}
          display={Display.Inline}
          textAlign={TextAlign.Right}
          color={Color.ColdGrey_800}
        >
          <Bold>{currentPrice}</Bold>
        </Text>
      ) : null}
    </Box>
  )
}

const ManageDeliveryChevronButton = withCitrusTheme(Box, () => ({
  cursor: 'pointer',
}))

type MobileManageDeliveryToggleProps = {
  isManageDeliveryExpanded: boolean
  onToggle: () => void
}

const MobileManageDeliveryToggle = ({
  isManageDeliveryExpanded,
  onToggle,
}: MobileManageDeliveryToggleProps) => (
  <ManageDeliveryChevronButton
    role="button"
    tabIndex={0}
    aria-label="Toggle manage delivery details"
    aria-expanded={isManageDeliveryExpanded}
    aria-controls="manage-delivery-panel"
    display={['flex', 'flex', 'none']}
    alignItems={AlignItems.Center}
    justifyContent={JustifyContent.FlexEnd}
    onClick={onToggle}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onToggle()
      }
    }}
  >
    <Icon
      name={isManageDeliveryExpanded ? 'chevron_up' : 'chevron_down'}
      variant={IconVariant.Actionable}
      size={8}
    />
  </ManageDeliveryChevronButton>
)

type OrderTileHeaderProps = {
  currentPrice: string | null
  isManageDeliveryExpanded: boolean
  onManageDeliveryClick: () => void
  order: DashboardExperienceOrderResponse
  originalPrice: string | null
  priceKind: string | null
}

export const OrderTileHeader = ({
  currentPrice,
  isManageDeliveryExpanded,
  onManageDeliveryClick,
  order,
  originalPrice,
  priceKind,
}: OrderTileHeaderProps) => {
  const displayedStateTitle = getDisplayedStateTitle(order)
  const stateTitleColor = getOrderStateTitleColor(order)
  const hasSummaryText = Boolean(order.summary_text)

  return (
    <Box
      width="100%"
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      alignItems={AlignItems.FlexStart}
    >
      <Box
        width="100%"
        display={Display.Flex}
        flexDirection={FlexDirection.Row}
        justifyContent={JustifyContent.SpaceBetween}
        alignItems={AlignItems.FlexStart}
      >
        <Box
          display={['flex', 'flex', 'none']}
          flexDirection={FlexDirection.Column}
          alignItems={AlignItems.FlexStart}
          gap={1}
        >
          <Heading2 size={[3, 5]}>{order.title}</Heading2>
          <Body2 color={stateTitleColor} fontWeight={FontWeight.Bold}>
            {displayedStateTitle}
          </Body2>
        </Box>
        <Box
          display={['none', 'none', 'flex']}
          flexDirection={FlexDirection.Column}
          alignItems={AlignItems.FlexStart}
          gap={1}
        >
          <Heading2 size={[3, 5]}>{order.title}</Heading2>
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Row}
            alignItems={AlignItems.Center}
            gap={3}
          >
            <Body2 color={stateTitleColor} fontWeight={FontWeight.Bold}>
              {displayedStateTitle}
            </Body2>
            {hasSummaryText ? (
              <>
                <Body2 color={Color.ColdGrey_800}>|</Body2>
                <Body2 color={Color.ColdGrey_800}>{order.summary_text}</Body2>
              </>
            ) : null}
          </Box>
        </Box>
        <OrderTilePrice
          currentPrice={currentPrice}
          originalPrice={originalPrice}
          priceKind={priceKind}
        />
      </Box>
      <Space size={1} direction="vertical" />
      <Box
        display={['flex', 'flex', 'none']}
        width="100%"
        flexDirection={FlexDirection.Row}
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.SpaceBetween}
        gap={3}
      >
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Row}
          alignItems={AlignItems.Center}
          gap={3}
        >
          {hasSummaryText ? <Body2 color={Color.ColdGrey_800}>{order.summary_text}</Body2> : null}
        </Box>
        <MobileManageDeliveryToggle
          isManageDeliveryExpanded={isManageDeliveryExpanded}
          onToggle={onManageDeliveryClick}
        />
      </Box>
    </Box>
  )
}
